import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * YmsAlertDialog — Matches Xamarin AlertDialog.xaml layout.
 *
 * Xamarin layout (AlertDialog.xaml):
 *  - Frame: CornerRadius=8, Padding=0, bg=#FFFFFF
 *  - Grid RowDefinitions="Auto, Auto, Auto":
 *    - Row 0: Header bg=#003366 (PrimaryBackgroundColor), Label Padding=15, TextColor=White
 *    - Row 1: Body HeightRequest=100 (phone=90), WidthRequest=420 (phone=350), Label Padding=15
 *    - Row 2: Button aligned right, PrimaryButton style (bg=#003366, text=white, 100x40, CornerRadius=4)
 *
 * Title logic (AlertDialogViewModel.cs):
 *  - Success → "Alert"
 *  - Error → "Warning!"
 *  - Warning → "Error"
 *  - Default → "Alert"
 *
 * This component supports two modes:
 *  1. Alert mode (showCancel=false): Single "Ok" button — user acknowledges
 *  2. Confirm mode (showCancel=true): "Cancel" + "Ok" buttons — user can accept or reject
 */

interface YmsAlertDialogProps {
  open: boolean;
  title: string;
  message: string;
  okText?: string;
  cancelText?: string;
  showCancel?: boolean;
  isLoading?: boolean;
  onOk: () => void;
  onCancel?: () => void;
}

export function YmsAlertDialog({
  open,
  title,
  message,
  okText,
  cancelText,
  showCancel = false,
  isLoading = false,
  onOk,
  onCancel,
}: YmsAlertDialogProps) {
  const { t } = useTranslation();
  const resolvedOkText = okText ?? t('common.ok');
  const resolvedCancelText = cancelText ?? t('common.cancel');
  if (!open) return null;

  return (
    /* Xamarin: PopupPage overlays full screen with semi-transparent bg */
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 animate-in fade-in duration-150">
      {/* Xamarin: Frame CornerRadius=8, Padding=0, bg=White */}
      <div className="w-[350px] bg-white rounded-lg overflow-hidden shadow-xl animate-in zoom-in-95 duration-150">
        {/* Row 0: Header — Xamarin: bg=#003366 (PrimaryBackgroundColor) */}
        <div className="bg-[#003366] px-[15px] py-[15px]">
          {/* Xamarin: Label Padding=15, TextColor=White */}
          <p className="text-white font-poppins font-semibold text-base">{title}</p>
        </div>

        {/* Row 1: Body — Xamarin: HeightRequest=90 (phone), WidthRequest=350 (phone) */}
        <div className="min-h-[90px] px-[15px] py-[15px] flex items-start">
          {/* Xamarin: Label Padding=15, PoppinsRegular */}
          <p className="text-sm font-poppins text-task-header-text">{message}</p>
        </div>

        {/* Row 2: Buttons — Xamarin: HorizontalOptions=EndAndExpand, Margin=15,0,15,15 */}
        <div className="flex justify-end gap-3 px-[15px] pb-[15px]">
          {showCancel && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="h-10 w-[100px] rounded-[4px] text-sm font-poppins font-semibold border border-[#003366] text-[#003366] bg-transparent disabled:opacity-50"
            >
              {resolvedCancelText}
            </button>
          )}
          {/* Xamarin: PrimaryButton style — bg=#003366, text=white, 100x40, CornerRadius=4 */}
          <button
            type="button"
            onClick={onOk}
            disabled={isLoading}
            className="h-10 w-[100px] rounded-[4px] text-sm font-poppins font-semibold bg-[#003366] text-white disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : resolvedOkText}
          </button>
        </div>
      </div>
    </div>
  );
}
